import type {
  CodeableConcept,
  Coding,
  Extension,
  Patient,
  Reference,
  RequestGroup,
  Resource,
  ServiceRequest,
} from "@medplum/fhirtypes";
import {
  type AnsweredValue,
  asMemberId,
  asOrderId,
  asPatientId,
  asPlannedTrialId,
  asVisitId,
  InvalidOrderSetError,
  isMemberId,
  ORDER_STATUSES,
  type Order,
  type OrderSetId,
  type ParameterAnswer,
  type ParameterSource,
  type PlannedTrial,
  parsePinnedCanonical,
} from "@repo/domain";
import { codeSystemUrl, extensionUrl } from "@repo/fhir-systems";
import {
  ORDER_KIND_SYSTEM,
  ORDER_SET_KIND_SYSTEM,
  TREATMENT_CHOICE_CODE_SYSTEM,
} from "@repo/fhir-systems/code-systems";
import { OBSERVATION_LEVEL_URL } from "@repo/fhir-systems/extensions";
import {
  ORDER_SET_ENTITY_ID_SYSTEM,
  PATIENT_ENTITY_ID_SYSTEM,
  PRACTITIONER_ENTITY_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { unitFromQuantity, unitToQuantity } from "../../lib/ucum";
import { fhirIdentifierIdentity } from "../identity";
import { omitUndefined } from "../omit-undefined";
import { domainIdFromReference, referenceToIdentity } from "../references";
import type { RefContext } from "../types";
import { fp } from "./codec";
import { observationLevelsToFhir } from "./observation-level.shared";
import { oneOf, required } from "./read";

const SELF_SOURCE = "self";

/** Positive storage discriminator for the redesigned OrderSet aggregate. */

export const REDESIGNED_ORDER_SET_KIND = "redesigned-order-set";
export const orderSetIdentity = fhirIdentifierIdentity<OrderSetId>(ORDER_SET_ENTITY_ID_SYSTEM);

export function patientIdFromReference(row: RequestGroup, reference: Reference | undefined) {
  const domainId = reference?.identifier?.system === PATIENT_ENTITY_ID_SYSTEM ? reference.identifier.value : undefined;
  return asPatientId(required(row, "patientId", domainId));
}

export function patientReference(patientId: string, ctx?: RefContext): Reference<Patient> {
  return referenceToIdentity(ctx, "Patient", PATIENT_ENTITY_ID_SYSTEM, patientId) as Reference<Patient>;
}

/** Stable identity crossing for mappers that reference an OrderSet anchor. */
export function orderSetIdFromFhir(group: RequestGroup): OrderSetId {
  return orderSetIdentity.read(group);
}

export function orderSetKindTag(): Coding {
  return { system: ORDER_SET_KIND_SYSTEM, code: REDESIGNED_ORDER_SET_KIND };
}

export function orderSetKindOf(resource: { meta?: { tag?: Coding[] } }): string | undefined {
  return resource.meta?.tag?.find((tag) => tag.system === ORDER_SET_KIND_SYSTEM)?.code;
}

/** One answered parameter. Sub-extensions below; `value[x]` per the answer's type. */
export const ORDER_PARAMETER_VALUE_URL = extensionUrl("order-parameter-value");
/** One planned trial: an `id` sub-extension plus its answers, each an `ORDER_PARAMETER_VALUE_URL`. */
export const ORDER_PLANNED_TRIAL_URL = extensionUrl("order-planned-trial");
/** Stable domain member identity, deliberately independent from ServiceRequest.id. */
export const ORDER_ID_URL = extensionUrl("order-identity");
/** Stable identity of the anticipated Procedure that will fulfil this order. */
export const ORDER_PROCEDURE_ID_URL = extensionUrl("order-procedure-identity");
/** The protocol occurrence that produced the order. Absent means curation-added. */
export const ORDER_SOURCE_OCCURRENCE_URL = extensionUrl("order-source-occurrence");
/** When the order was withdrawn. */
export const ORDER_REVOKED_AT_URL = extensionUrl("order-revoked-at");
/** Display suffix telling repeated orders of one definition apart. Not identity. */
export const ORDER_QUALIFIER_URL = extensionUrl("order-qualifier");
/** The delta entry's changes, one JSON document per entry. */
export const ORDER_SET_DELTA_CHANGES_URL = extensionUrl("order-set-delta-changes");
/** Care plan whose curation owns this set; absent for standalone order sets. */
export const ORDER_SET_ENVELOPE_URL = extensionUrl("order-set-envelope-minutes");
/** Authored step offset on its RequestGroup container action. */
export const ORDER_SET_STEP_OFFSET_URL = extensionUrl("order-set-step-offset-minutes");

/**
 * Medplum REJECTS an empty RequestGroup container action under rqg-1: an action
 * must have a resource or child action, but not both. Offset and timingDuration
 * alone do not satisfy that constraint. Empty steps therefore retain their
 * placement in this extension; populated steps use container actions. Do not
 * unify these encodings by emitting empty actions: that makes the write fail.
 */
export const ORDER_SET_EMPTY_STEP_URL = extensionUrl("order-set-empty-step");
/** Highest step member identity ever issued, including removed steps. */
export const ORDER_SET_STEP_ID_HIGH_URL = extensionUrl("order-set-step-id-high");
/** Zero-based ordering among the care plan's curated order sets. */
export const ORDER_SET_CURATION_RANK_URL = extensionUrl("order-set-curation-rank");
/**
 * What a draft replaced, as JSON. Written once when the draft opens and dropped
 * when it is saved or discarded, so it costs one copy per draft version only
 * while a set is being curated.
 */
export const ORDER_SET_DRAFT_BASELINE_URL = extensionUrl("order-set-draft-baseline");
/** Exact aggregate cardinality, used by read models to reject staggered partial graphs. */
export const ORDER_SET_MEMBER_COUNT_URL = extensionUrl("order-set-member-count");

export const PROCEDURE_ORDER = "procedure";

/** The `Basic.code` discriminator for a delta entry. Mapper-owned; never a domain field. */
export const ORDER_SET_DELTA_CODE: CodeableConcept = {
  coding: [{ system: codeSystemUrl("record-kind"), code: "order-set-delta" }],
};

// No `reasonUrl`: a delta records WHAT was curated, and nothing asks the
// clinician why. Storing a reason nobody can supply left a field that read as
// supported on the way in and was always empty on the way out.
export const ORDER_SET_DELTA_CONTRACT = {
  code: ORDER_SET_DELTA_CODE,
  changesUrl: ORDER_SET_DELTA_CHANGES_URL,
  authorIdentitySystem: PRACTITIONER_ENTITY_ID_SYSTEM,
};

// ── parameter values ─────────────────────────────────────────────────────────

/**
 * The kind is written EXPLICITLY on every entry rather than inferred from which
 * `value[x]` is present, because a Choice has no single `value[x]` — it is a
 * repeating selection — and inference would make an empty multi-select
 * indistinguishable from a malformed row.
 *
 * `url` is the OUTER wrapper only; the body below is the one spelling of an
 * answered parameter in storage. The Procedure's attested copy reuses this
 * function under its own url, so the two encodings cannot drift — there is one
 * encoder, not two that have to be kept in step.
 */
export function parameterValueToFhir(parameter: ParameterAnswer, url: string = ORDER_PARAMETER_VALUE_URL): Extension {
  const value = parameter.value;
  const sub: Extension[] = [
    { url: "source", valueString: parameter.source },
    { url: "parameterId", valueString: parameter.parameterId },
    { url: "kind", valueCode: value.kind },
  ];
  switch (value.kind) {
    case "Quantity":
      sub.push({ url: "value", valueQuantity: omitUndefined({ value: value.value, ...unitToQuantity(value.unit) }) });
      break;
    case "Integer":
      sub.push({ url: "value", valueInteger: value.value });
      break;
    case "Boolean":
      sub.push({ url: "value", valueBoolean: value.value });
      break;
    case "Text":
      sub.push({ url: "value", valueString: value.value });
      break;
    case "Choice":
      for (const selection of value.selected) sub.push({ url: "selection", valueString: selection });
      if (value.freeText !== undefined) sub.push({ url: "freeText", valueString: value.freeText });
      break;
  }
  return { url, extension: sub };
}

/**
 * Fail loudly rather than silently dropping a malformed stored answer. Reads
 * the sub-extension body and ignores the outer url, so it decodes an order's
 * answers and a procedure's attested copy alike. Carry the containing row so
 * malformed selections identify their actual resource type and locator.
 */
export function parameterValueFromFhir(extension: Extension, row: Resource): ParameterAnswer {
  const orderId = row.id;
  const sub = extension.extension ?? [];
  const of = (url: string) => sub.find((e) => e.url === url);
  const source = of("source")?.valueString;
  const parameterId = of("parameterId")?.valueString;
  const kind = of("kind")?.valueCode;
  if (!source || !parameterId || !kind) {
    throw new InvalidOrderSetError("A stored parameter value is missing its source, parameter id or kind", { orderId });
  }
  const value = of("value");
  const recorded = ((): AnsweredValue => {
    switch (kind) {
      case "Quantity":
        return omitUndefined({
          kind: "Quantity" as const,
          value: requireNumber(value?.valueQuantity?.value, kind, orderId),
          unit: unitFromQuantity(value?.valueQuantity),
        });
      case "Integer":
        return { kind: "Integer", value: requireNumber(value?.valueInteger, kind, orderId) };
      case "Boolean":
        if (typeof value?.valueBoolean !== "boolean") {
          throw new InvalidOrderSetError("A stored Boolean parameter value has no boolean", { orderId });
        }
        return { kind: "Boolean", value: value.valueBoolean };
      case "Text":
        if (value?.valueString === undefined) {
          throw new InvalidOrderSetError("A stored Text parameter value has no string", { orderId });
        }
        return { kind: "Text", value: value.valueString };
      case "Choice":
        return omitUndefined({
          kind: "Choice" as const,
          selected: sub
            .filter((e) => e.url === "selection")
            .map((e) => asMemberId(required(row, "parameterValues.selected", e.valueString))),
          freeText: of("freeText")?.valueString,
        });
      default:
        throw new InvalidOrderSetError(`'${kind}' is not a parameter value kind`, {
          orderId,
          parameterId: asMemberId(parameterId),
        });
    }
  })();
  return { source: sourceFromFhir(source), parameterId: asMemberId(parameterId), value: recorded };
}

function plannedTrialToFhir(trial: PlannedTrial): Extension {
  return {
    url: ORDER_PLANNED_TRIAL_URL,
    extension: [{ url: "id", valueString: trial.id }, ...trial.values.map((value) => parameterValueToFhir(value))],
  };
}

export function plannedTrialFromFhir(extension: Extension, row: ServiceRequest): PlannedTrial {
  const sub = extension.extension ?? [];
  return {
    id: asPlannedTrialId(required(row, "plannedTrial.id", sub.find((e) => e.url === "id")?.valueString)),
    values: sub.filter((e) => e.url === ORDER_PARAMETER_VALUE_URL).map((e) => parameterValueFromFhir(e, row)),
  };
}

function requireNumber(value: number | undefined, kind: string, orderId: string | undefined): number {
  if (typeof value !== "number") {
    throw new InvalidOrderSetError(`A stored ${kind} parameter value has no number`, { orderId });
  }
  return value;
}

/**
 * The sentinel and a member id are both plain strings in storage, and they
 * cannot collide: a member id is `<prefix>-<n>`, which the sentinel is not.
 */
function sourceFromFhir(source: string): ParameterSource {
  if (source === SELF_SOURCE) return SELF_SOURCE;
  if (isMemberId(source)) return asMemberId(source);
  throw new InvalidOrderSetError(`'${source}' is neither the procedure nor a device requirement identity`);
}

/**
 * `extension` is OWNED, so it is rebuilt whole — that is what lets clearing the
 * last parameter actually reach FHIR. Foreign extensions are carried across by
 * hand, because owning the field means an absent one would delete them.
 */
export function orderExtensions(order: Order, prior: ServiceRequest | undefined): Extension[] | undefined {
  const ours = new Set<string>([
    ORDER_ID_URL,
    ORDER_PROCEDURE_ID_URL,
    ORDER_PARAMETER_VALUE_URL,
    ORDER_PLANNED_TRIAL_URL,
    OBSERVATION_LEVEL_URL,
    ORDER_SOURCE_OCCURRENCE_URL,
    ORDER_REVOKED_AT_URL,
    ORDER_QUALIFIER_URL,
  ]);
  const foreign = (prior?.extension ?? []).filter((e) => !ours.has(e.url));
  const extensions: Extension[] = [
    ...foreign,
    { url: ORDER_ID_URL, valueString: order.orderId },
    ...order.parameterValues.map((value) => parameterValueToFhir(value)),
    ...(order.plannedTrials ?? []).map(plannedTrialToFhir),
    ...observationLevelsToFhir(order.observationLevels),
  ];
  if (order.sourceOccurrenceId) {
    extensions.push({ url: ORDER_SOURCE_OCCURRENCE_URL, valueString: order.sourceOccurrenceId });
  }
  if (order.qualifier) {
    extensions.push({ url: ORDER_QUALIFIER_URL, valueString: order.qualifier });
  }
  if (order.revokedAt) extensions.push({ url: ORDER_REVOKED_AT_URL, valueDateTime: order.revokedAt });
  return extensions.length > 0 ? extensions : undefined;
}

function kindCategory(code: string): CodeableConcept {
  return { coding: [{ system: ORDER_KIND_SYSTEM, code }] };
}

/**
 * `category` is owned because the procedure marker must be replaceable,
 * but the ServiceRequest may also carry categories written by an integration.
 * Keep every concept that is not this mapper's discriminator.
 */
export function categoriesToFhir(code: string, prior: ServiceRequest | undefined): CodeableConcept[] {
  const foreign = (prior?.category ?? []).filter(
    (concept) => !concept.coding?.some((coding) => coding.system === ORDER_KIND_SYSTEM),
  );
  return [...foreign, kindCategory(code)];
}

export function kindOfRow(row: ServiceRequest): string | undefined {
  for (const concept of row.category ?? []) {
    const coding = concept.coding?.find((c) => c.system === ORDER_KIND_SYSTEM);
    if (coding?.code) return coding.code;
  }
  return undefined;
}

export function commonFromFhir(row: ServiceRequest) {
  const storedOrderId = row.extension?.find((e) => e.url === ORDER_ID_URL)?.valueString;
  const sourceOccurrenceId = row.extension?.find((e) => e.url === ORDER_SOURCE_OCCURRENCE_URL)?.valueString;
  return {
    orderId: asOrderId(required(row, "orderId", storedOrderId)),
    definition: parsePinnedCanonical(row.instantiatesCanonical?.[0]),
    sourceOccurrenceId: sourceOccurrenceId ? asMemberId(sourceOccurrenceId) : undefined,
    status: oneOf(row, "status", row.status, ORDER_STATUSES),
    revokedAt: row.extension?.find((e) => e.url === ORDER_REVOKED_AT_URL)?.valueDateTime,
    visitId: row.encounter
      ? asVisitId(required(row, "visitId", domainIdFromReference(VISIT_ENTITY_ID_SYSTEM, row.encounter)))
      : undefined,
  };
}

/** Child rows cannot infer their owner or patient; the mapper supplies these resolved graph links. */
export interface OrderRowContext {
  patient: Reference<Patient>;
  /** The owning set, by locator and identity. */
  group: NonNullable<ServiceRequest["basedOn"]>[number];
}

// Owned lists must clear when empty; a merge would otherwise retain stale answers.
export const SERVICE_REQUEST_OWNS = ["extension", "instantiatesCanonical", "status", "category"].map(fp);
export const commonOrderReads = {
  orderId: [fp(`extension.where(url = '${ORDER_ID_URL}').valueString`)],
  definition: [fp("instantiatesCanonical[0]")],
  sourceOccurrenceId: [fp(`extension.where(url = '${ORDER_SOURCE_OCCURRENCE_URL}').valueString`)],
  status: [fp("status")],
  revokedAt: [fp(`extension.where(url = '${ORDER_REVOKED_AT_URL}').valueDateTime`)],
  visitId: [fp("encounter")],
};

/** Native component values with the order's parameter identity and typed answer encoding. */
export function parameterComponentToFhir(answer: ParameterAnswer): import("@medplum/fhirtypes").ObservationComponent {
  const encoded = parameterValueToFhir(answer);
  const sub = encoded.extension ?? [];
  const value = sub.find((entry) => entry.url === "value");
  const { url: _url, ...slots } = value ?? { url: "value" };
  return {
    code: { coding: [{ system: codeSystemUrl("treatment-trial-parameter"), code: answer.parameterId }] },
    extension: [
      { ...encoded, extension: sub.filter((entry) => !["value", "selection", "freeText"].includes(entry.url)) },
    ],
    ...slots,
    ...(answer.value.kind === "Choice"
      ? {
          valueCodeableConcept: {
            coding: sub
              .filter((entry) => entry.url === "selection")
              .map((entry) => ({ system: TREATMENT_CHOICE_CODE_SYSTEM, code: entry.valueString })),
            ...(answer.value.freeText === undefined ? {} : { text: answer.value.freeText }),
          },
        }
      : {}),
  };
}

export function parameterComponentFromFhir(
  component: import("@medplum/fhirtypes").ObservationComponent,
  row: Resource,
): ParameterAnswer {
  const identity = component.extension?.find((entry) => entry.url === ORDER_PARAMETER_VALUE_URL);
  const sub = [...(identity?.extension ?? [])];
  const kind = sub.find((entry) => entry.url === "kind")?.valueCode;
  if (kind === "Choice") {
    for (const coding of component.valueCodeableConcept?.coding ?? []) {
      if (coding.system === TREATMENT_CHOICE_CODE_SYSTEM) sub.push({ url: "selection", valueString: coding.code });
    }
    if (component.valueCodeableConcept?.text !== undefined)
      sub.push({ url: "freeText", valueString: component.valueCodeableConcept.text });
  } else {
    sub.push({
      url: "value",
      valueQuantity: component.valueQuantity,
      valueInteger: component.valueInteger,
      valueBoolean: component.valueBoolean,
      valueString: component.valueString,
    });
  }
  return parameterValueFromFhir({ url: ORDER_PARAMETER_VALUE_URL, extension: sub }, row);
}
